/** Internal order fields - not shown in customer-facing trip chat. */
const INTERNAL_FIELDS = new Set([
  "commission",
  "driverpay",
  "vat",
  "cpkcents",
  "collectionlat",
  "collectionlng",
  "deliverylat",
  "deliverylng",
]);

const FIELD_LABELS = {
  collectiondate: "Collection date",
  collectiontime: "Collection time",
  runners: "Helpers",
  floors: "Floors",
  description: "Load description",
  firstname: "First name",
  lastname: "Last name",
  promocode: "Promo code",
  servicefee: "Service fee",
  sellingprice: "Trip price",
  collectionaddress: "Pick-up address",
  deliveryaddress: "Drop-off address",
  tripdistance: "Trip distance",
  tripduration: "Trip duration",
  trucktype: "Vehicle type",
  businessname: "Business name",
  businessaddress: "Business address",
  businessvatnumber: "VAT number",
  businessregistration: "Registration number",
};

const MONEY_FIELDS = new Set(["servicefee", "sellingprice", "commission", "driverpay", "vat"]);

const zar = (cents) =>
  new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
  }).format(Number(cents) / 100);

function humanizeField(field) {
  return String(field || "")
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());
}

function formatChangeValue(field, value) {
  if (value === null || value === undefined || value === "") return "-";

  if (MONEY_FIELDS.has(field)) {
    const cents = Number(value);
    if (!Number.isNaN(cents)) return zar(cents);
  }

  if (field === "tripdistance") {
    const metres = Number(value);
    if (!Number.isNaN(metres)) return `${(metres / 1000).toFixed(1)} km`;
  }

  if (field === "tripduration") {
    const seconds = Number(value);
    if (!Number.isNaN(seconds)) {
      const mins = Math.round(seconds / 60);
      if (mins >= 60) {
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        return m > 0 ? `${h}h ${m}m` : `${h}h`;
      }
      return `${mins} min`;
    }
  }

  return String(value);
}

/**
 * Parse a trip chat message for customer-facing display.
 * @returns {{ kind: 'plain', text: string } | { kind: 'order-updated', lines: Array<{field,label,from,to}> } | { kind: 'price-change-request', oldPrice?: string, newPrice?: string, reason: string }}
 */
export function formatTripChatMessage(raw) {
  const text = String(raw || "")
    .replace(/^Driver::/, "")
    .trim();

  if (!text.startsWith("{")) {
    return { kind: "plain", text };
  }

  try {
    const parsed = JSON.parse(text);

    if (parsed?.type === "order-updated" && parsed.changes) {
      const lines = Object.entries(parsed.changes)
        .filter(([field]) => !INTERNAL_FIELDS.has(field))
        .map(([field, change]) => ({
          field,
          label: FIELD_LABELS[field] || humanizeField(field),
          from: formatChangeValue(field, change?.from),
          to: formatChangeValue(field, change?.to),
        }));

      if (lines.length === 0) {
        return {
          kind: "plain",
          text: "Your booking was updated by our team.",
        };
      }

      return { kind: "order-updated", lines };
    }

    if (parsed?.type === "price-change-request" && parsed.reason) {
      return {
        kind: "price-change-request",
        oldPrice: parsed.oldPrice,
        newPrice: parsed.newPrice,
        reason: parsed.reason,
      };
    }
  } catch (_) {
    // fall through
  }

  return { kind: "plain", text };
}
